import {
  createReapproCategory,
  updateReapproCategory,
  useProducts,
  useReapproCategories,
  useReapproCategory,
} from '@gozoki/api'
import {
  useReapproCategoryForm,
  type ReapproCategoryForm,
} from '@gozoki/api/src/forms/useReapproCategoryForm'
import { Button, IconButton, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import z from 'zod'
import { usePermissionGuard, withRoutePermissions } from '~/utils/rbac'
import LoadingPaper from '../../../components/containers/LoadingPaper'
import ProductDualListSelectorRhf, {
  type ProductDualListItem,
} from '../../../components/inputs/ProductDualListSelectorRhf'
import TextFieldRhf from '../../../components/inputs/TextFieldRhf'
import { TimePickerRhf } from '../../../components/inputs/TimePickerRhf'
import Page from '../../../components/Page'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

export const Route = createFileRoute('/_authenticated/categories/reappro/new')({
  staticData: {
    permission: ['read', 'categories.reappro.category'],
  },
  component: RouteComponent,
  validateSearch: z.object({
    reapproCategoryId: z.number().optional(),
  }),
})
withRoutePermissions(Route)

function RouteComponent() {
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()

  const { reapproCategoryId } = Route.useSearch()
  const updateMode = reapproCategoryId !== undefined // Update mode if there is a category ID in the URL
  const canAdd = usePermissionGuard(['add', 'categories.reappro.category']).authorized
  const canEdit = usePermissionGuard(['edit', 'categories.reappro.category']).authorized
  const canSubmit = updateMode ? canEdit : canAdd

  // If this is update mode, use isLoading to show a loading indicator before the data arrives
  const { data, isLoading } = useReapproCategory(reapproCategoryId)
  const { data: reapproCategories } = useReapproCategories()
  const { data: products } = useProducts()

  const { handleSubmit, control, trigger, setValue } = useReapproCategoryForm({})
  const watchedProductIds = useWatch({
    control,
    name: 'productIds',
  })
  const activeProducts = useMemo(
    () => (products ?? []).filter((product) => product.isActive),
    [products],
  )

  const conflictingProducts = useMemo(() => {
    const selectedProductIds = watchedProductIds ?? []
    if (selectedProductIds.length === 0) {
      return []
    }

    return activeProducts
      .filter((product) => selectedProductIds.includes(product.id))
      .filter((product) => {
        if (!product.reapproCategoryId) {
          return false
        }
        if (!updateMode) {
          return true
        }
        return product.reapproCategoryId !== reapproCategoryId
      })
      .map((product) => ({
        productLabel: product.label,
        categoryLabel:
          reapproCategories?.find((category) => category.id === product.reapproCategoryId)?.name ??
          'Catégorie inconnue',
      }))
  }, [activeProducts, watchedProductIds, updateMode, reapproCategories, reapproCategoryId])

  const reapproCategoryLabelById = useMemo(
    () =>
      (reapproCategories ?? []).reduce(
        (acc, category) => {
          acc[category.id] = category.name
          return acc
        },
        {} as Record<number, string>,
      ),
    [reapproCategories],
  )

  const productItems = useMemo<ProductDualListItem[]>(
    () =>
      activeProducts.map((product) => {
        const groupId = product.reapproCategoryId ?? 0
        return {
          id: product.id,
          label: product.label,
          groupId,
          groupLabel: reapproCategoryLabelById[groupId] ?? 'AUTRE',
        }
      }),
    [activeProducts, reapproCategoryLabelById],
  )

  // Fill the form with initial data once the data is fetched
  useEffect(() => {
    if (data) {
      setValue('name', data.name)
      setValue('daysBefore', data.daysBefore)
      setValue('computeHour', data.computeHour.slice(0, 5))
      setValue('sendHour', data.sendHour.slice(0, 5))
      setValue('productIds', data.products)
    }
  }, [setValue, data])

  const navigateBack = () => {
    queryClient.invalidateQueries({ queryKey: ['products'] })
    navigate({ to: '/categories/reappro' })
  }

  const { mutate: upload, isPending: isUploading } = useMutation({
    mutationFn: createReapproCategory,
    onSuccess: navigateBack,
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: (newData: ReapproCategoryForm) =>
      updateReapproCategory(reapproCategoryId ?? 0, newData),
    onSuccess: navigateBack,
  })

  return (
    <Page sx={{ maxWidth: 'min(1200px, 95vw)', margin: 'auto', width: '100%' }}>
      <form onSubmit={handleSubmit(updateMode ? (v) => update(v) : (v) => upload(v))}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={navigateBack}>
            <ArrowLeft
              style={{
                color: ThemeColors.BLACK,
                height: '24px',
                width: '24px',
              }}
            />
          </IconButton>
          <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
            {updateMode
              ? `${canSubmit ? 'Modifier' : 'Voir'} une catégorie de reapprovisionnement`
              : 'Ajouter une catégorie de reapprovisionnement'}
          </Typography>

          <Button
            variant="outlined"
            sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
            onClick={navigateBack}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit ? true : isUploading || isUpdating}
          >
            {updateMode ? 'Modifier' : 'Ajouter'}
          </Button>
        </div>
        <LoadingPaper
          sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
          loading={isLoading && updateMode}
        >
          <Typography mb="8px" style={fonts.inputHeader}>
            Nom
          </Typography>
          <TextFieldRhf
            variant="outlined"
            label="Nom de la catégorie"
            control={control}
            trigger={trigger}
            name="name"
            fullWidth
          />

          <Typography mb="8px" mt="12px" style={fonts.inputHeader}>
            J-x
          </Typography>
          <TextFieldRhf
            variant="outlined"
            label="J-x"
            placeholder="2"
            control={control}
            trigger={trigger}
            name="daysBefore"
            fullWidth
          />

          <Typography mb="8px" mt="12px" style={fonts.inputHeader}>
            Heure de calcul
          </Typography>
          <TimePickerRhf
            label="Heure de calcul"
            control={control}
            trigger={trigger}
            name="computeHour"
          />

          <Typography mb="8px" mt="12px" style={fonts.inputHeader}>
            Heure d'envoi
          </Typography>
          <TimePickerRhf
            label="Heure d'envoi"
            control={control}
            trigger={trigger}
            name="sendHour"
          />

          <Typography mb="8px" mt="12px" style={fonts.inputHeader}>
            Produits
          </Typography>
          <ProductDualListSelectorRhf
            control={control}
            name="productIds"
            items={productItems}
            disabled={!canSubmit}
          />

          {conflictingProducts.length > 0 && (
            <>
              <Typography
                mt="8px"
                style={{ ...fonts.informationSubText, color: ThemeColors.ORANGE }}
              >
                Attention : les produits ci-dessous sont déjà dans une autre catégorie et seront
                déplacés.
              </Typography>
              <Typography
                component="ul"
                mt="6px"
                sx={{
                  ...fonts.informationSubText,
                  color: ThemeColors.ORANGE,
                  paddingLeft: '20px',
                  margin: 0,
                }}
              >
                {conflictingProducts.map((product) => (
                  <li key={`${product.productLabel}-${product.categoryLabel}`}>
                    {product.productLabel} — {product.categoryLabel}
                  </li>
                ))}
              </Typography>
            </>
          )}
        </LoadingPaper>
      </form>
    </Page>
  )
}
